import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { restaurant } from "../mock";

/**
 * Datenschutzerklärung. Edit the lines in DATENSCHUTZ below.
 * `host` must name the company that serves the website once it is live.
 */
const DATENSCHUTZ = {
  company: "Corte Camatti GbR",
  responsible: "Luca D’Alconzo, Roberto Lisignoli",
  host: "IONOS SE, Elgendorfer Straße 57, 56410 Montabaur, Deutschland",
  stand: "Oktober 2026",
};

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl font-600 text-[#2A2420] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export default function DatenschutzPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Datenschutzerklärung – Quattro Tavoli";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-[#FAF4EA] min-h-screen">
      <Navbar solid />

      <article className="max-w-3xl mx-auto px-6 pt-36 pb-24">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-[#6b6058] hover:text-[#9C3B2E] transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
        </button>

        <h1 className="font-display text-4xl sm:text-5xl font-700 text-[#2A2420]">
          Datenschutzerklärung
        </h1>
        <p className="mt-3 text-sm text-[#9c9188]">
          Informationen zur Verarbeitung personenbezogener Daten gemäß Art. 13 DSGVO
          · Stand: {DATENSCHUTZ.stand}
        </p>

        <div className="mt-12 space-y-10 text-[#4A423B] font-body leading-relaxed">
          <Section title="Verantwortlicher">
            <p>
              {DATENSCHUTZ.company}
              <br />
              {restaurant.address}
              <br />
              Deutschland
            </p>
            <p>
              Vertreten durch: {DATENSCHUTZ.responsible}
              <br />
              Telefon: {restaurant.phone}
              <br />
              E-Mail:{" "}
              <a href={`mailto:${restaurant.email}`} className="text-[#9C3B2E] hover:underline">
                {restaurant.email}
              </a>
            </p>
          </Section>

          <Section title="Reservierungs- und Eventanfragen">
            <p>
              Wenn Sie das Reservierungsformular oder das Formular für Events und
              Catering nutzen, verarbeiten wir die von Ihnen eingegebenen Daten:
              Name, E-Mail-Adresse, Telefonnummer, gewünschtes Datum und Uhrzeit,
              Anzahl der Gäste sowie Ihre Nachricht.
            </p>
            <p>
              Zweck der Verarbeitung ist ausschließlich die Bearbeitung und
              Beantwortung Ihrer Anfrage. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b
              DSGVO, da die Verarbeitung zur Anbahnung eines Vertrags erfolgt,
              sowie Art. 6 Abs. 1 lit. f DSGVO an unserem berechtigten Interesse,
              Anfragen zuverlässig beantworten zu können.
            </p>
            <p>
              Wir geben diese Daten nicht zu Werbezwecken weiter. Ihre Anfrage
              verbleibt in unserem E-Mail-Postfach, bis sie zur Erfüllung des
              Zwecks nicht mehr erforderlich ist und keine gesetzlichen
              Aufbewahrungspflichten entgegenstehen.
            </p>
          </Section>

          <Section title="Formularversand über FormSubmit">
            <p>
              Für die Übermittlung der Formulare setzen wir den Dienst FormSubmit
              (formsubmit.co) ein. Ihre Eingaben werden an die Server dieses
              Anbieters übertragen und von dort als E-Mail an unser Postfach
              weitergeleitet. Dabei wird auch Ihre IP-Adresse verarbeitet. Eine
              Übermittlung in Länder außerhalb der Europäischen Union kann nicht
              ausgeschlossen werden.
            </p>
            <p>
              Nach dem Absenden erhalten Sie außerdem eine automatische
              Eingangsbestätigung an die von Ihnen angegebene E-Mail-Adresse. Auch
              diese E-Mail wird über FormSubmit versendet. Sie bestätigt nur den
              Eingang Ihrer Anfrage, nicht die Reservierung selbst; diese
              bestätigen wir Ihnen gesondert.
            </p>
            <p>
              Rechtsgrundlage ist Art. 6 Abs. 1 lit. b und lit. f DSGVO. Wenn Sie
              diese Übermittlung vermeiden möchten, können Sie uns Ihre Anfrage
              jederzeit stattdessen telefonisch unter {restaurant.phone} oder direkt
              per E-Mail zusenden.
            </p>
          </Section>

          <Section title="Hosting">
            <p>
              Diese Website wird bei einem externen Dienstleister gehostet:{" "}
              {DATENSCHUTZ.host}. Beim Aufruf der Seite erhebt der Anbieter
              technisch notwendige Zugriffsdaten in Server-Logfiles, darunter
              IP-Adresse, Datum und Uhrzeit des Zugriffs, aufgerufene Seite,
              übertragene Datenmenge sowie Browser- und Betriebssystemangaben.
            </p>
            <p>
              Diese Verarbeitung dient dem sicheren und stabilen Betrieb der
              Website. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Mit dem
              Hosting-Anbieter besteht ein Vertrag zur Auftragsverarbeitung
              gemäß Art. 28 DSGVO.
            </p>
          </Section>

          <Section title="Google Maps">
            <p>
              Auf der Startseite ist eine Karte von Google Maps eingebunden, damit
              Sie unseren Standort finden. Anbieter ist Google Ireland Limited,
              Gordon House, Barrow Street, Dublin 4, Irland. Beim Laden der Karte
              wird Ihre IP-Adresse an Google übertragen; eine Übermittlung in die
              USA ist möglich.
            </p>
            <p>
              Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO an unserem
              berechtigten Interesse, die Anfahrt zu unserem Bistro verständlich
              darzustellen. Weitere Informationen finden Sie in der
              Datenschutzerklärung von Google:{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9C3B2E] hover:underline break-all"
              >
                https://policies.google.com/privacy
              </a>
            </p>
          </Section>


          <Section title="Cookies und Reichweitenmessung">
            <p>
              Diese Website verwendet keine Cookies zu Werbe- oder Analysezwecken
              und bindet keine Tracking- oder Statistikdienste ein. Lediglich die
              von Ihnen gewählte Sprache wird lokal in Ihrem Browser gespeichert
              (Local Storage), damit sie beim nächsten Besuch erhalten bleibt.
              Diese Angabe wird nicht an uns übertragen und lässt keinen Rückschluss
              auf Ihre Person zu.
            </p>
          </Section>

          <Section title="Ihre Rechte">
            <p>
              Sie haben das Recht auf Auskunft über die zu Ihrer Person
              gespeicherten Daten (Art. 15 DSGVO), auf Berichtigung (Art. 16
              DSGVO), auf Löschung (Art. 17 DSGVO), auf Einschränkung der
              Verarbeitung (Art. 18 DSGVO), auf Datenübertragbarkeit (Art. 20
              DSGVO) sowie ein Widerspruchsrecht gegen Verarbeitungen auf
              Grundlage von Art. 6 Abs. 1 lit. f DSGVO (Art. 21 DSGVO).
            </p>
            <p>
              Für alle Anliegen genügt eine Nachricht an{" "}
              <a href={`mailto:${restaurant.email}`} className="text-[#9C3B2E] hover:underline">
                {restaurant.email}
              </a>
              .
            </p>
            <p>
              Außerdem können Sie sich bei einer Datenschutz-Aufsichtsbehörde
              beschweren. Für uns zuständig ist das Bayerische Landesamt für
              Datenschutzaufsicht, Promenade 18, 91522 Ansbach.
            </p>
          </Section>

          <Section title="Sicherheit">
            <p>
              Die Übertragung der Daten auf dieser Website erfolgt verschlüsselt
              über HTTPS. Wir treffen angemessene technische und organisatorische
              Maßnahmen, um Ihre Daten gegen unbefugten Zugriff und Verlust zu
              schützen.
            </p>
          </Section>
        </div>
      </article>

      <Footer />
    </main>
  );
}